import React, { useCallback, useMemo, useState } from 'react';
import { useTranslate } from '@translations/useTranslate';
import { IFOIRequestForm } from '@interfaces/FOIRequest.interface';
import { IFormItemType } from '@interfaces/FormItemType';
import { useFormik } from 'formik';
import { CustomButton, CustomInputFeedback, FormContainer } from '@ui/components/atoms';
import { SubFormSection } from '@ui/components/molecules/SubFormSection';
import { Form } from 'react-bootstrap';
import ArrowIcon from '@assets/svg/arrow.svg';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSubmitFOIRequest } from '@services/FOIRequest/hooks/useSubmitFOIRequest';
import { freedomOfInformationFormValidationScheme } from './FOIRequestForm.scheme';

export interface FOIRequestFormProps {
  onRequestSuccess: (jiraTicketId: string, freedomRequestId: string) => void;
  onRequestFailed: () => void;
}
export const FOIRequestForm: React.FC<FOIRequestFormProps> = ({ onRequestSuccess }) => {
  const { t } = useTranslate();
  const recaptchaRef = React.useRef<ReCAPTCHA | undefined>();
  const [recaptcha, setRecaptcha] = React.useState<ReCAPTCHA | undefined>();
  const [error, setError] = React.useState<string | undefined>();
  const [isChecked, setIsChecked] = useState(false);
  const [validateConsent, setValidateConsent] = useState(false);
  const { mutateAsync: submitRequest, isLoading } = useSubmitFOIRequest();
  React.useEffect(() => {
    if (recaptchaRef.current) {
      setRecaptcha(recaptchaRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recaptchaRef.current]);

  const FOIFormValidationScheme = useMemo(() => freedomOfInformationFormValidationScheme(t), [t]);
  const formik = useFormik<IFOIRequestForm>({
    initialValues: {
      title: '',
      purposeOfRequest: '',
      description: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      recaptchaToken: '',
    },
    onSubmit: useCallback((values) => {
      const mappedValues: IFOIRequestForm = Object.fromEntries(
        Object.entries(values).map(([key, value]) => {
          return [key, value === '' ? undefined : value];
        })
      ) as unknown as IFOIRequestForm;

      const formData = new FormData();
      Object.entries(mappedValues).forEach(([key, value]) => {
        formData.append(key, value ?? '');
      });
      submitRequest(formData, {
        onError: () => {
          setError(t('global.error'));
        },
      })
        .then((response) => {
          onRequestSuccess(response.freedomRequestId, response.jiraTicketId);
        })
        .finally(() => {
          if (recaptcha) {
            recaptcha.reset();
          }
        });
    }, []),
    validationSchema: FOIFormValidationScheme,
    validateOnChange: false,
    validateOnBlur: true,
  });
  const userDetailsFormItems: IFormItemType[] = useMemo(() => {
    const formItems: IFormItemType[] = [
      {
        id: 'title',
        label: t('foi.request.title.label'),
        placeholder: t('foi.request.title.placeholder'),
        type: 'text',
        required: true,
        value: formik.values.title,
        isTouched: formik.touched.title,
        isValid: !formik.errors.title,
        errorFeedback: formik.errors.title,
        maxLength: 100,
        onChange: (value) => {
          formik.setFieldValue('title', value);
        },
        onBlur: () => formik.setFieldTouched('title', true),
      },
      {
        id: 'purposeOfRequest',
        label: t('foi.request.purposeOfRequest.label'),
        placeholder: t('foi.request.purposeOfRequest.placeholder'),
        type: 'text',
        required: true,
        value: formik.values.purposeOfRequest,
        isTouched: formik.touched.purposeOfRequest,
        isValid: !formik.errors.purposeOfRequest,
        errorFeedback: formik.errors.purposeOfRequest,
        maxLength: 100,
        onChange: (value) => {
          formik.setFieldValue('purposeOfRequest', value);
        },
        onBlur: () => formik.setFieldTouched('purposeOfRequest', true),
      },
      {
        id: 'firstName',
        label: t('foi.request.firstName.label'),
        placeholder: t('foi.request.firstName.placeholder'),
        type: 'text',
        required: true,
        value: formik.values.firstName,
        isTouched: formik.touched.firstName,
        isValid: !formik.errors.firstName,
        errorFeedback: formik.errors.firstName,
        maxLength: 100,
        onChange: (value) => {
          formik.setFieldValue('firstName', value);
        },
        onBlur: () => formik.setFieldTouched('firstName', true),
      },
      {
        id: 'lastName',
        label: t('foi.request.lastName.label'),
        placeholder: t('foi.request.lastName.placeholder'),
        type: 'text',
        required: true,
        value: formik.values.lastName,
        isTouched: formik.touched.lastName,
        isValid: !formik.errors.lastName,
        errorFeedback: formik.errors.lastName,
        maxLength: 100,
        onChange: (value) => {
          formik.setFieldValue('lastName', value);
        },
        onBlur: () => formik.setFieldTouched('lastName', true),
      },
      {
        id: 'email',
        label: t('foi.request.Email.label'),
        placeholder: t('foi.request.Email.placeholder'),
        type: 'text',
        required: true,
        value: formik.values.email,
        isTouched: formik.touched.email,
        isValid: !formik.errors.email,
        errorFeedback: formik.errors.email,
        maxLength: 100,
        onChange: (value) => {
          formik.setFieldValue('email', value);
        },
        onBlur: () => formik.setFieldTouched('email', true),
      },
      {
        id: 'phoneNumber',
        label: t('foi.form.phone.label'),
        placeholder: '+966xxxxxxxx',
        type: 'number',
        required: true,
        value: formik.values.phoneNumber,
        isTouched: formik.touched.phoneNumber,
        isValid: !formik.errors.phoneNumber,
        errorFeedback: formik.errors.phoneNumber,
        maxLength: 15,
        onChange: (value) => formik.setFieldValue('phoneNumber', value),
        onBlur: () => formik.setFieldTouched('phoneNumber', true),
        note: t('foi.form.phone.note'),
      },
      {
        id: 'description',
        label: t('foi.request.Description.label'),
        placeholder: t('foi.request.Description.label'),
        type: 'textarea',
        required: true,
        value: formik.values.description,
        isTouched: formik.touched.description,
        isValid: !formik.errors.description,
        errorFeedback: formik.errors.description,
        maxLength: 400,
        onChange: (value) => {
          formik.setFieldValue('description', value);
        },
        onBlur: () => formik.setFieldTouched('description', true),
      },
    ];
    return formItems;
  }, [formik]);
  return (
    <FormContainer error={error}>
      <div className="w-100 pb-5 d-flex justify-content-center">
        <a
          href="https://www.tawal.com.sa/en/faqs"
          target="_blank"
          rel="noopener noreferrer"
          className="text-dark fw-semibold d-inline"
        >
          {t('foi.request.faq.link')}
        </a>
      </div>
      <SubFormSection items={userDetailsFormItems} />
      <Form.Label
        className="custom-form-labelmb-2 "
        style={{
          color: '#606F8A',
          fontSize: '14px',
          lineHeight: '22px',
          fontWeight: '600',
        }}
      >
        <span className="fw-semibold" style={{ color: '#FC4C02' }}>
          *
        </span>{' '}
        {t('privacy.notice.title')}
      </Form.Label>
      <Form.Group className="px-3 mb-5">
        <Form.Control as="div" className="border-0 w-auto p-0 d-flex gap-2">
          <Form.Check
            type="checkbox"
            label={<></>}
            value="consent-check"
            required={true}
            onChange={(event) => setIsChecked(event.target.checked)}
            name="privacyNotice"
          />
          <div className="d-flex flex-column">
            <Form.Label htmlFor="privacyNotice">
              {' '}
              {t('request.privacyNotice.label')}
              <a
                href="https://www.tawal.com.sa/en/privacy-notice"
                target="_blank"
                rel="noopener noreferrer"
                className="text-dark fw-semibold d-inline"
              >
                {t('request.privacyNotice')}
              </a>
            </Form.Label>
            {!isChecked && validateConsent ? (
              <div className="invalid-feedback d-block mt-1">{t('foi.form.policy.error')}</div>
            ) : null}
          </div>
        </Form.Control>
      </Form.Group>
      <Form.Group className="w-100 pb-3 px-3 d-flex flex-column justify-content-start align-items-start">
        <Form.Control
          as="div"
          className="border-0 w-auto p-0"
          isInvalid={!!formik.errors.recaptchaToken && formik.touched.recaptchaToken}
        >
          <ReCAPTCHA
            ref={recaptchaRef as React.LegacyRef<ReCAPTCHA>}
            sitekey="6LdRwy4qAAAAAIN7oQMsZevTuDmW2m302maO2hwf"
            onChange={(token) => {
              formik.setFieldValue('recaptchaToken', token ?? '');
              formik.validateField('recaptchaToken');
            }}
          />
        </Form.Control>
        {formik.errors.recaptchaToken && (
          <CustomInputFeedback errorFeedback={formik.errors.recaptchaToken} />
        )}
      </Form.Group>
      <div className="w-100 pt-3 pb-5 d-flex justify-content-center align-items-center">
        <CustomButton
          variant="primary"
          text={t('submit')}
          disabled={false}
          suffixIcon={ArrowIcon}
          onClick={() => {
            setValidateConsent(true);
            formik.handleSubmit();
          }}
        />
      </div>
    </FormContainer>
  );
};
